import { useMemo } from 'react';
import { useSelector } from '@legendapp/state/react';
import { usePrice, formatPriceSimple } from '@kidslabs/module-catalog';
import { currencyFormatter } from '@kidslabs/module-core';
import { useHybridCartPrices } from './use-hybrid-cart-prices';
import { calculateOriginalTotalFromItems } from '../helpers';
import { cartStoreAPI } from '../stores';
import type { CartInformationDiscountItem } from '../components/cart-information/cart-information.types';

export interface CartPriceSummaryResult {
    /** Product-only grand total (excludes shipping) */
    productOnlyGrandTotal: number;
    /** Original total before any discounts */
    originalTotal: number;
    /** Formatted savings amount (original - grandTotal), undefined if no savings */
    savingsAmount: string | undefined;
    /** Formatted grand total price string */
    grandTotalPrice: string;
    /** Formatted base (subtotal) price string */
    subTotalPrice: string;
    /** Formatted discount items array */
    discounts: CartInformationDiscountItem[];
    /** Price confidence level from hybrid cart prices */
    priceConfidenceLevel: ReturnType<typeof useHybridCartPrices>['priceConfidenceLevel'];
    /** Whether server price fetching has failed consistently */
    serverPriceError: ReturnType<typeof useHybridCartPrices>['serverPriceError'];
    /** Retry callback for server price fetching */
    retryServerPrices: ReturnType<typeof useHybridCartPrices>['retryServerPrices'];
    /** Selected cart items from hybrid cart prices */
    selectedCartItems: ReturnType<typeof useHybridCartPrices>['selectedCartItems'];
    /** Raw prices object from hybrid cart prices */
    prices: ReturnType<typeof useHybridCartPrices>['prices'];
    /** Shipping fee from hybrid cart prices */
    shippingFee: ReturnType<typeof useHybridCartPrices>['shippingFee'];
}

/**
 * Shared hook that consolidates price calculation logic used by both
 * CartTotal (mobile bottom bar) and CartInformation (desktop sidebar).
 *
 * Single source of truth for:
 * - Product-only grand total (excludes shipping)
 * - Original total (regular prices before discounts)
 * - Savings amount calculation
 * - Discount items formatting
 * - Price confidence level
 *
 * @param translationFunction - i18n translation function for discount labels
 */
export const useCartPriceSummary = (translationFunction: (key: string) => string): CartPriceSummaryResult => {
    const { prices, shippingFee, selectedCartItems, priceConfidenceLevel, serverPriceError, retryServerPrices } =
        useHybridCartPrices();

    // Stock availability map for consistent filtering
    const stockAvailabilityMap = useSelector(() => cartStoreAPI.state.getAvailableItemsObservable().get());

    // Product-only grand total (excludes shipping).
    // When shippingFee is available, subtract it from grand_total.
    // When shippingFee is unavailable (server data stale), fall back to
    // subtotal_with_discount_excluding_tax which NEVER includes shipping,
    // preventing the race condition where grand_total includes shipping but
    // shippingFee is undefined (causing shipping to be silently included).
    const productOnlyGrandTotal = useMemo(() => {
        const rawGrandTotal = prices.grand_total?.value;
        // Use nullish check instead of falsy check:
        // grandTotal === 0 is valid (e.g. 100% discount cart).
        if (rawGrandTotal == null) {
            return 0;
        }

        if (shippingFee != null) {
            // Server data fresh: subtract shipping from grand_total
            return rawGrandTotal - (shippingFee.amount ?? 0);
        }

        // Shipping fee unavailable (stale data): use subtotal_with_discount
        // which is product subtotal after discounts, guaranteed to exclude shipping.
        const subtotalWithDiscount = prices.subtotal_with_discount_excluding_tax?.value;
        if (subtotalWithDiscount != null) {
            return subtotalWithDiscount;
        }

        // Final fallback: use subtotal_excluding_tax (no discounts, no shipping)
        return prices.subtotal_excluding_tax?.value ?? 0;
    }, [
        prices.grand_total?.value,
        prices.subtotal_with_discount_excluding_tax?.value,
        prices.subtotal_excluding_tax?.value,
        shippingFee,
    ]);

    // Calculate original total using shared utility (aligned across CartTotal & CartInformation)
    const originalTotal = useMemo(() => {
        return calculateOriginalTotalFromItems(selectedCartItems, stockAvailabilityMap);
    }, [selectedCartItems, stockAvailabilityMap]);

    const { price: grandTotalPrice, basePrice: subTotalPrice } = usePrice({
        amount: productOnlyGrandTotal,
        baseAmount: prices.subtotal_excluding_tax?.value ?? 0,
    });

    // True savings = original total - product grand total (excludes shipping).
    // Compares only product prices to avoid shipping inflating the displayed total.
    const savingsAmount = useMemo(() => {
        const savings = originalTotal - productOnlyGrandTotal;
        if (savings <= 0) return undefined;
        return formatPriceSimple(savings);
    }, [productOnlyGrandTotal, originalTotal]);

    // Format discount items from server prices
    const discounts = useMemo(() => {
        if (!prices?.discounts) {
            return [];
        }

        let totalDiscountAmount = 0;
        const discountList = prices.discounts.map(discount => {
            totalDiscountAmount += discount?.amount.value ?? 0;
            return {
                title: translationFunction('text_discount'),
                value: '-' + currencyFormatter(discount?.amount.value ?? 0),
            };
        });
        const totalDiscount = {
            title: translationFunction('text_discount'),
            value: '-' + currencyFormatter(totalDiscountAmount),
        };
        return [totalDiscount, ...discountList];
    }, [prices?.discounts, translationFunction]) as CartInformationDiscountItem[];

    return {
        productOnlyGrandTotal,
        originalTotal,
        savingsAmount,
        grandTotalPrice,
        subTotalPrice: subTotalPrice ?? grandTotalPrice,
        discounts,
        priceConfidenceLevel,
        serverPriceError,
        retryServerPrices,
        selectedCartItems,
        prices,
        shippingFee,
    };
};
